import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Search,
  Filter,
  FileText,
  Building2,
  HardHat,
  User,
  CheckCircle,
  XCircle,
  Clock,
  Plus,
  RefreshCw,
  CreditCard,
  Pencil,
} from 'lucide-react';
import {
  GlassCard,
  GlassButton,
  GlassInput,
  GlassSelect,
  GlassTable,
  GlassModal,
  GlassTextarea,
  FadeIn,
} from '../../components/ui';
import { api } from '../../hooks/useApi';

const BOOKS = {
  imobiliaria: { name: 'Imobiliaria', color: '#22c55e', icon: Building2 },
  construtora: { name: 'Construtora', color: '#3b82f6', icon: HardHat },
  pessoal: { name: 'Pessoal', color: '#8b5cf6', icon: User },
  geral: { name: 'Geral', color: '#94a3b8', icon: FileText },
};

const INITIAL_FORM = {
  tipo: 'despesa',
  valor: '',
  data_vencimento: new Date().toISOString().slice(0, 10),
  status: 'pendente',
  descricao: '',
  pessoa_nome: '',
  pessoa_tipo: '',
  numero_documento: '',
  conta_id: '',
  categoria_id: '',
  obra_id: '',
  imovel_id: '',
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(`${value}T00:00:00`).toLocaleDateString('pt-BR');
};

const normalizeText = (value) => String(value || '').toLowerCase();

const toOptions = (items, labelKeys = ['nome']) => (
  items || []
).map((item) => ({
  value: item.id,
  label: labelKeys.map((key) => item[key]).filter(Boolean).join(' - ') || item.id,
}));

export const FinanceiroTransacoes = () => {
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, pages: 0 });
  const [filters, setFilters] = useState({ book: 'all', status: 'all', tipo: 'all', search: '' });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [options, setOptions] = useState({ contas: [], categorias: [], obras: [], imoveis: [] });
  const [error, setError] = useState('');

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { per_page: 200 };
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.tipo !== 'all') params.tipo = filters.tipo;
      const response = await api.get('/contabilidade/movimentacoes', { params });
      setTransactions(response.data.items || []);
      setPagination({ total: response.data.total || 0, pages: response.data.pages || 0 });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao carregar movimentacoes.');
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.tipo]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [contasRes, categoriasRes, obrasRes, imoveisRes] = await Promise.all([
          api.get('/contabilidade/contas-bancarias'),
          api.get('/contabilidade/plano-contas'),
          api.get('/construtora/obras', { params: { per_page: 200 } }),
          api.get('/imobiliaria/imoveis', { params: { per_page: 200 } }),
        ]);
        setOptions({
          contas: toOptions(contasRes.data.items, ['nome', 'banco']),
          categorias: toOptions(categoriasRes.data.items, ['codigo', 'nome']),
          obras: toOptions(obrasRes.data.items, ['nome']),
          imoveis: toOptions(imoveisRes.data.items, ['codigo', 'titulo']),
        });
      } catch (err) {
        console.warn('Erro ao carregar opcoes financeiras', err);
      }
    };
    loadOptions();
  }, []);

  const filteredTransactions = useMemo(() => {
    return transactions.filter((item) => {
      const book = item.book || 'geral';
      if (filters.book !== 'all' && book !== filters.book) return false;
      if (filters.search) {
        const haystack = [
          item.descricao,
          item.pessoa_nome,
          item.numero_documento,
          item.categoria,
          item.conta,
        ].map(normalizeText).join(' ');
        if (!haystack.includes(normalizeText(filters.search))) return false;
      }
      return true;
    });
  }, [filters.book, filters.search, transactions]);

  const handleChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const openCreateModal = () => {
    setSelectedTransaction(null);
    setForm(INITIAL_FORM);
    setIsCreateModalOpen(true);
  };

  const openEditModal = (row) => {
    setSelectedTransaction(row);
    setForm({
      tipo: row.tipo || 'despesa',
      valor: row.valor ?? '',
      data_vencimento: row.data_vencimento || new Date().toISOString().slice(0, 10),
      status: row.status || 'pendente',
      descricao: row.descricao || '',
      pessoa_nome: row.pessoa_nome || '',
      pessoa_tipo: row.pessoa_tipo || '',
      numero_documento: row.numero_documento || '',
      conta_id: row.conta_id || '',
      categoria_id: row.categoria_id || '',
      obra_id: row.obra_id || '',
      imovel_id: row.imovel_id || '',
    });
    setIsCreateModalOpen(true);
  };

  const closeFormModal = () => {
    setIsCreateModalOpen(false);
    setSelectedTransaction(null);
    setForm(INITIAL_FORM);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        valor: Number(String(form.valor).replace(',', '.')),
      };
      if (payload.obra_id) payload.imovel_id = '';
      if (payload.imovel_id) payload.obra_id = '';
      if (selectedTransaction) {
        await api.put(`/contabilidade/movimentacoes/${selectedTransaction.id}`, payload);
      } else {
        await api.post('/contabilidade/movimentacoes', payload);
      }
      closeFormModal();
      await loadTransactions();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao salvar movimentacao.');
    } finally {
      setSaving(false);
    }
  };

  const handlePay = async (row) => {
    setPayingId(row.id);
    setError('');
    try {
      await api.post(`/contabilidade/movimentacoes/${row.id}/pagar`, {
        data_pagamento: new Date().toISOString().slice(0, 10),
      });
      await loadTransactions();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao dar baixa na movimentacao.');
    } finally {
      setPayingId(null);
    }
  };

  const columns = [
    {
      key: 'data_vencimento',
      title: 'Vencimento',
      accessor: 'data_vencimento',
      render: (row) => formatDate(row.data_vencimento),
    },
    {
      key: 'descricao',
      title: 'Descricao',
      accessor: 'descricao',
      render: (row) => (
        <div>
          <p className="text-white/90">{row.descricao || row.pessoa_nome || 'Lancamento sem descricao'}</p>
          <p className="text-xs text-white/40">{row.pessoa_nome || row.numero_documento || row.conta || '-'}</p>
        </div>
      ),
    },
    {
      key: 'book',
      title: 'Book',
      accessor: 'book',
      render: (row) => {
        const book = BOOKS[row.book] || BOOKS.geral;
        return (
          <div className="flex items-center gap-2">
            {React.createElement(book.icon, {
              className: 'w-4 h-4',
              style: { color: book.color },
            })}
            <span style={{ color: book.color }}>{book.name}</span>
          </div>
        );
      },
    },
    {
      key: 'tipo',
      title: 'Tipo',
      accessor: 'tipo',
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs ${
          row.tipo === 'receita'
            ? 'bg-green-500/20 text-green-400'
            : row.tipo === 'despesa'
              ? 'bg-red-500/20 text-red-400'
              : 'bg-blue-500/20 text-blue-400'
        }`}>
          {row.tipo || '-'}
        </span>
      ),
    },
    {
      key: 'valor',
      title: 'Valor',
      accessor: 'valor',
      render: (row) => {
        const signed = row.tipo === 'despesa' ? -Number(row.valor || 0) : Number(row.valor || 0);
        return (
          <span className={signed >= 0 ? 'text-green-400' : 'text-red-400'}>
            {formatCurrency(signed)}
          </span>
        );
      },
    },
    {
      key: 'status',
      title: 'Status',
      accessor: 'status',
      render: (row) => {
        const statusConfig = {
          pago: { icon: CheckCircle, label: 'Pago', className: 'text-green-400' },
          pendente: { icon: Clock, label: 'Pendente', className: 'text-yellow-400' },
          cancelado: { icon: XCircle, label: 'Cancelado', className: 'text-red-400' },
        };
        const config = statusConfig[row.status] || statusConfig.pendente;
        return (
          <span className={`flex items-center gap-1 text-xs ${config.className}`}>
            {React.createElement(config.icon, { className: 'w-4 h-4' })}
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'actions',
      title: 'Acoes',
      render: (row) => (
        <div className="flex items-center gap-2">
          <GlassButton
            size="xs"
            variant="secondary"
            icon={Pencil}
            onClick={(event) => {
              event.stopPropagation();
              openEditModal(row);
            }}
          >
            Editar
          </GlassButton>
          {row.status === 'pendente' && (
            <GlassButton
              size="xs"
              variant="success"
              icon={CreditCard}
              loading={payingId === row.id}
              onClick={(event) => {
                event.stopPropagation();
                handlePay(row);
              }}
            >
              Baixar
            </GlassButton>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Transacoes</h1>
          <p className="text-white/50">
            {loading ? 'Carregando movimentacoes reais...' : `${filteredTransactions.length} de ${pagination.total} movimentacoes`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <GlassButton variant="secondary" icon={RefreshCw} onClick={loadTransactions} loading={loading}>
            Atualizar
          </GlassButton>
          <GlassButton icon={Plus} onClick={openCreateModal}>
            Nova transacao
          </GlassButton>
        </div>
      </div>

      {error && (
        <GlassCard className="p-4 border-red-500/30 bg-red-500/10" hover={false}>
          <p className="text-sm text-red-300">{error}</p>
        </GlassCard>
      )}

      <FadeIn>
        <GlassCard className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <GlassInput
              icon={Search}
              placeholder="Buscar por descricao, pessoa, documento..."
              className="max-w-sm"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />

            <GlassSelect
              value={filters.book}
              onChange={(e) => setFilters({ ...filters, book: e.target.value })}
              placeholder=""
              className="min-w-[170px]"
              options={[
                { value: 'all', label: 'Todos os books' },
                { value: 'imobiliaria', label: 'Imobiliaria' },
                { value: 'construtora', label: 'Construtora' },
                { value: 'pessoal', label: 'Pessoal' },
                { value: 'geral', label: 'Geral' },
              ]}
            />

            <GlassSelect
              value={filters.tipo}
              onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
              placeholder=""
              className="min-w-[150px]"
              options={[
                { value: 'all', label: 'Todos os tipos' },
                { value: 'receita', label: 'Receitas' },
                { value: 'despesa', label: 'Despesas' },
                { value: 'transferencia', label: 'Transferencias' },
              ]}
            />

            <GlassSelect
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              placeholder=""
              className="min-w-[150px]"
              options={[
                { value: 'all', label: 'Todos os status' },
                { value: 'pendente', label: 'Pendente' },
                { value: 'pago', label: 'Pago' },
                { value: 'cancelado', label: 'Cancelado' },
              ]}
            />

            <GlassButton variant="ghost" icon={Filter}>
              Filtros
            </GlassButton>
          </div>
        </GlassCard>
      </FadeIn>

      <FadeIn delay={0.2}>
        <GlassCard>
          <GlassTable
            columns={columns}
            data={filteredTransactions}
            searchable={false}
            emptyMessage={loading ? 'Carregando movimentacoes...' : 'Nenhuma movimentacao encontrada'}
          />
        </GlassCard>
      </FadeIn>

      <GlassModal
        isOpen={isCreateModalOpen}
        onClose={closeFormModal}
        title={selectedTransaction ? 'Editar transacao' : 'Nova transacao'}
        icon={selectedTransaction ? Pencil : Plus}
        size="lg"
        footer={
          <>
            <GlassButton variant="ghost" onClick={closeFormModal}>
              Cancelar
            </GlassButton>
            <GlassButton onClick={handleSave} loading={saving}>
              Salvar
            </GlassButton>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GlassSelect
            label="Tipo"
            value={form.tipo}
            onChange={(e) => handleChange('tipo', e.target.value)}
            placeholder=""
            options={[
              { value: 'receita', label: 'Receita' },
              { value: 'despesa', label: 'Despesa' },
              { value: 'transferencia', label: 'Transferencia' },
            ]}
          />
          <GlassInput
            label="Valor"
            type="number"
            min="0"
            step="0.01"
            value={form.valor}
            onChange={(e) => handleChange('valor', e.target.value)}
            placeholder="0,00"
          />
          <GlassInput
            label="Data de vencimento"
            type="date"
            value={form.data_vencimento}
            onChange={(e) => handleChange('data_vencimento', e.target.value)}
          />
          <GlassSelect
            label="Status"
            value={form.status}
            onChange={(e) => handleChange('status', e.target.value)}
            placeholder=""
            options={[
              { value: 'pendente', label: 'Pendente' },
              { value: 'pago', label: 'Pago' },
              { value: 'cancelado', label: 'Cancelado' },
            ]}
          />
          <div className="md:col-span-2">
            <GlassTextarea
              label="Descricao"
              rows={3}
              value={form.descricao}
              onChange={(e) => handleChange('descricao', e.target.value)}
              placeholder="Ex.: pagamento fornecedor, recebimento cliente..."
            />
          </div>
          <GlassSelect
            label="Conta bancaria"
            value={form.conta_id}
            onChange={(e) => handleChange('conta_id', e.target.value)}
            placeholder="Sem conta"
            options={options.contas}
          />
          <GlassSelect
            label="Plano de contas"
            value={form.categoria_id}
            onChange={(e) => handleChange('categoria_id', e.target.value)}
            placeholder="Sem categoria"
            options={options.categorias}
          />
          <GlassInput
            label="Pessoa"
            value={form.pessoa_nome}
            onChange={(e) => handleChange('pessoa_nome', e.target.value)}
            placeholder="Cliente, fornecedor ou funcionario"
          />
          <GlassSelect
            label="Tipo de pessoa"
            value={form.pessoa_tipo}
            onChange={(e) => handleChange('pessoa_tipo', e.target.value)}
            placeholder="Nao informado"
            options={[
              { value: 'cliente', label: 'Cliente' },
              { value: 'fornecedor', label: 'Fornecedor' },
              { value: 'funcionario', label: 'Funcionario' },
              { value: 'colaborador', label: 'Colaborador' },
            ]}
          />
          <GlassInput
            label="Documento"
            value={form.numero_documento}
            onChange={(e) => handleChange('numero_documento', e.target.value)}
            placeholder="NF, recibo, contrato..."
          />
          <GlassSelect
            label="Obra"
            value={form.obra_id}
            onChange={(e) => setForm((current) => ({ ...current, obra_id: e.target.value, imovel_id: e.target.value ? '' : current.imovel_id }))}
            placeholder="Sem obra"
            options={options.obras}
          />
          <GlassSelect
            label="Imovel"
            value={form.imovel_id}
            onChange={(e) => setForm((current) => ({ ...current, imovel_id: e.target.value, obra_id: e.target.value ? '' : current.obra_id }))}
            placeholder="Sem imovel"
            options={options.imoveis}
          />
        </div>
      </GlassModal>
    </div>
  );
};

export default FinanceiroTransacoes;
